import React, { useCallback, useEffect, useRef, useState } from "react";
import KuikaCopyButton from "../../../../ui-kit/kuika-copy-button/kuika-copy-button";

/**
 * Rating encoding — dashboard-compatible. The ratings telemetry aggregates a
 * 1-5 scale; OK/Not OK map onto the two poles so existing views keep working.
 */
const RATING_OK = 5;
const RATING_NOT_OK = 1;

const NOT_OK_REASONS: readonly string[] = ["Did not understand", "Wrong change", "Bad design", "Other"];

interface McpChatMessageFooterProps {
  chatRequestId?: string;
  content: string;
  userRating?: number;
  /** The latest assistant message keeps its footer visible without hover. */
  isLast?: boolean;
  onRate?: (chatRequestId: string, rating: number, reason?: string) => void;
}

const McpChatMessageFooter: React.FC<McpChatMessageFooterProps> = ({
  chatRequestId,
  content,
  userRating,
  isLast,
  onRate
}) => {
  const [reasonMenuOpen, setReasonMenuOpen] = useState<boolean>(false);
  const reasonWrapRef = useRef<HTMLDivElement | null>(null);

  const rated: boolean = userRating != null;
  const isOk: boolean = userRating === RATING_OK;
  const isNotOk: boolean = userRating === RATING_NOT_OK;

  useEffect(() => {
    if (!reasonMenuOpen) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      if (reasonWrapRef.current && !reasonWrapRef.current.contains(e.target as Node)) {
        setReasonMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [reasonMenuOpen]);

  const handleOk = useCallback((): void => {
    if (rated || !chatRequestId || !onRate) return;
    onRate(chatRequestId, RATING_OK);
  }, [rated, chatRequestId, onRate]);

  const handleNotOkClick = useCallback((): void => {
    if (rated || !chatRequestId) return;
    setReasonMenuOpen((prev: boolean): boolean => !prev);
  }, [rated, chatRequestId]);

  const handleReasonSelect = useCallback(
    (reason: string): void => {
      setReasonMenuOpen(false);
      if (!chatRequestId || !onRate) return;
      onRate(chatRequestId, RATING_NOT_OK, reason);
    },
    [chatRequestId, onRate]
  );

  // When the reason menu is open the footer must lift above sibling messages.
  // The footer is a positioned, z-indexed element (its own stacking context),
  // so the menu's inner z-index can't escape it — raising the footer itself is
  // what puts the popover over the next message.
  const footerClass = `mcp-chat__msg-footer${isLast || rated ? " mcp-chat__msg-footer--pinned" : ""}${
    reasonMenuOpen ? " mcp-chat__msg-footer--menu-open" : ""
  }`;
  const showRating: boolean = chatRequestId != null && onRate != null;

  return (
    <div className={footerClass}>
      {showRating && (
        <button
          type="button"
          className={`mcp-chat__msg-action mcp-chat__msg-action--ok${isOk ? " mcp-chat__msg-action--active" : ""}`}
          aria-label="Good response"
          aria-pressed={isOk}
          disabled={rated}
          onClick={handleOk}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill={isOk ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 12h3v9H4z" />
            <path d="M7 12l4-8c1.3 0 2.1 1 2 2.2L12.6 9H18c1.1 0 1.9 1 1.7 2l-1.3 6c-.2.9-1 1.6-1.9 1.6H7" />
          </svg>
        </button>
      )}
      {showRating && (
        <div className="mcp-chat__msg-action-wrap" ref={reasonWrapRef}>
          <button
            type="button"
            className={`mcp-chat__msg-action mcp-chat__msg-action--notok${isNotOk ? " mcp-chat__msg-action--active" : ""}`}
            aria-label="Bad response"
            aria-pressed={isNotOk}
            aria-haspopup="menu"
            aria-expanded={reasonMenuOpen}
            disabled={rated}
            onClick={handleNotOkClick}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill={isNotOk ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 12h-3V3h3z" />
              <path d="M17 12l-4 8c-1.3 0-2.1-1-2-2.2L11.4 15H6c-1.1 0-1.9-1-1.7-2l1.3-6C5.8 6.1 6.6 5.4 7.5 5.4H17" />
            </svg>
          </button>
          {reasonMenuOpen && (
            <div className="mcp-chat__msg-reason-menu" role="menu">
              <div className="mcp-chat__msg-reason-title">What went wrong?</div>
              {NOT_OK_REASONS.map((reason: string) => (
                <button
                  key={reason}
                  type="button"
                  role="menuitem"
                  className="mcp-chat__msg-reason-item"
                  onClick={() => handleReasonSelect(reason)}
                >
                  {reason}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* The copy glyph rolls to a check once the message is on the clipboard, then back. */}
      <KuikaCopyButton value={content} size="sm" label="Copy message" copiedLabel="Copied" />
    </div>
  );
};

export default McpChatMessageFooter;
